import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import PageTitle from "../../components/PageTitle";
import { Skeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import EmptyState from "../../components/ui/EmptyState";
import is_money from "../../helpers/is_money";
import swal from "../../helpers/swal";
import BtnCreditWallet from "../../button/CreditWallet";
import useAuthStore from "../../stores/useAuthStore";
import { FiCreditCard, FiPrinter, FiExternalLink, FiCheckCircle, FiPlus } from "react-icons/fi";

const MyWallet = () => {
  const queryClient = useQueryClient();
  const [verifying, setVerifying] = React.useState({});
  const { user: student } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ["myWalletDataset"],
    queryFn: async () => {
      const req = await axiosget(`ms_student_wallet_transactions`);
      return req?.data;
    },
  });

  const openReceipt = (trx) => window.open(`/wallet-receipt/${trx.trx}`, "_blank");

  const _verify = async (trx) => {
    try {
      setVerifying(prev => ({ ...prev, [trx]: true }));
      const req = await axiosget(`ms_student_wallet_transactions/verify-funding/${trx}`);
      const { type, message } = req.data;
      swal(type, message);
      if (type === "success") queryClient.invalidateQueries(["myWalletDataset"]);
    } catch (e) {
      swal("error", "Verification failed");
    } finally {
      setVerifying(prev => ({ ...prev, [trx]: false }));
    }
  };

  return (
    <div>
      <PageTitle title="My Wallet" sub="Save up funds to make future payments without worry" />

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-12 w-full" />
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {error && <Alert variant="danger"><AlertDescription>{error?.message || String(error)}</AlertDescription></Alert>}
      {data?.type !== undefined && <Alert variant="danger"><AlertDescription>{data?.message}</AlertDescription></Alert>}

      {/* Balance Card */}
      {data?.type === undefined && (
        <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 rounded-2xl p-6 sm:p-8 text-white shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <FiCreditCard className="w-7 h-7" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Available Balance</p>
              <p className="text-3xl sm:text-4xl font-extrabold mt-1">{is_money(data?.wallet?.amount)}</p>
            </div>
          </div>
          <BtnCreditWallet modern={true} />
        </div>
      )}

      {/* Empty State */}
      {data?.type === undefined && (!data?.find || data?.find?.length === 0) && (
        <EmptyState variant="no-data" title="No Transactions" description="You haven't performed any wallet transactions yet." />
      )}

      {/* Transaction Table */}
      {data?.type === undefined && data?.find?.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  {["#", "Transaction Code", "Amount", "Status", "Description", "Action"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data?.find?.map((v, i) => (
                  <tr key={`trx_${i}`} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-3.5 text-sm text-neutral-500 font-semibold">{i + 1}</td>
                    <td className="px-4 py-3.5">
                      <div className="text-sm font-bold text-neutral-800 font-mono">{v.trx}</div>
                      <div className="text-xs text-neutral-400 mt-0.5">{v.payment?.created_at}</div>
                    </td>
                    <td className={`px-4 py-3.5 text-sm font-bold ${v.trxtype ? 'text-success-600' : 'text-danger-500'}`}>
                      {v.trxtype ? '+' : '-'}{is_money(v.amount)}
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge variant={v.status === 'success' ? 'success' : 'warning'} size="sm" dot>
                        {v.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-neutral-600">{v.purpose || (v.trxtype ? "Deposit" : "Withdrawal")}</td>
                    <td className="px-4 py-3.5">
                      {v.status === 'pending' && v.trxtype && (
                        <div className="flex gap-2">
                          {v.payment?.paymentUrl && (
                            <a href={v.payment.paymentUrl} target="_blank" rel="noopener noreferrer">
                              <Button variant="primary" size="sm" leftIcon={<FiExternalLink className="w-3 h-3" />}>Pay</Button>
                            </a>
                          )}
                          <Button variant="outline" size="sm" onClick={() => _verify(v.trx)} loading={verifying[v.trx]} leftIcon={<FiCheckCircle className="w-3 h-3" />}>
                            Verify
                          </Button>
                        </div>
                      )}
                      {v.status === 'success' && (
                        <Button variant="outline" size="sm" onClick={() => openReceipt(v)} leftIcon={<FiPrinter className="w-3 h-3" />}>
                          Receipt
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MyWallet;
